import React, { useCallback } from "react";
import { Building, Banknote, Calendar, Download } from "@semo/icons";
import { cn } from "../utils/cn";
import { CONTRACT_STATUS_CONFIG, ContractStatus } from "./config";
import { formatDateForDisplay } from "../components/project-card/utils";
import { BaseDashboardCard } from "../components/dashboard-card/BaseDashboardCard";

export interface ContractCardProps {
  contractNumber: string;
  projectName: string;
  status: ContractStatus;
  totalAmount: number;
  createdAt: string;
  counterpartyName: string;
  counterpartyLabel?: string;
  pdfUrl?: string;
  onDownloadPdf?: () => void;
  onClick?: () => void;
  className?: string;
}

export const ContractCard = React.memo(function ContractCard({
  contractNumber,
  projectName,
  status,
  totalAmount,
  createdAt,
  counterpartyName,
  counterpartyLabel = "공급자",
  pdfUrl,
  onDownloadPdf,
  onClick,
  className,
}: ContractCardProps) {
  const statusConfig = CONTRACT_STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;
  const isPdfAvailable = !!pdfUrl;

  const handleDownloadPdf = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onDownloadPdf) {
        onDownloadPdf();
      }
    },
    [onDownloadPdf],
  );

  const infoItems = [
    {
      label: counterpartyLabel,
      value: counterpartyName || "정보 없음",
      icon: Building,
    },
    {
      label: "계약 금액",
      value: `${totalAmount.toLocaleString("ko-KR")}원`,
      icon: Banknote,
      valueColor: "text-primary font-semibold",
    },
    {
      label: "계약일",
      value: formatDateForDisplay(createdAt),
      icon: Calendar,
    },
  ];

  const footer = (
    <>
      <button
        onClick={handleDownloadPdf}
        disabled={!isPdfAvailable}
        className={cn(
          "flex-1 py-3 rounded-[12px] text-sm font-medium transition-all flex items-center justify-center gap-2 border",
          isPdfAvailable
            ? "bg-primary/5 text-primary border-primary/20 hover:bg-primary/10"
            : "bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed",
        )}
      >
        <Download className="w-4 h-4" />
        {isPdfAvailable ? "계약서 다운로드" : "생성 중..."}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className="flex-1 py-3 bg-white text-gray-700 border border-gray-200 rounded-[12px] text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
      >
        상세보기
      </button>
    </>
  );

  return (
    <BaseDashboardCard
      idLabel={contractNumber}
      statusBadge={
        <span
          className={cn(
            "h-[28px] px-2.5 rounded-[8px] text-2xs font-medium flex items-center justify-center gap-1.5 border",
            statusConfig.bgColor,
            statusConfig.textColor,
            statusConfig.borderColor,
          )}
        >
          <StatusIcon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="leading-none">{statusConfig.label}</span>
        </span>
      }
      title={projectName}
      infoItems={infoItems}
      footer={footer}
      onClick={onClick}
      className={className}
    />
  );
});
